import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Autocomplete, Box, CircularProgress, TextField, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ContactsIcon from '@mui/icons-material/ContactsOutlined';
import SchoolIcon from '@mui/icons-material/SchoolOutlined';
import { listLeads } from '../api/leads';
import { listStudents } from '../api/students';
import { useAuth } from '../context/authContext';

interface SearchOption {
  type: 'Lead' | 'Student';
  id: number;
  label: string;
  secondary: string;
  path: string;
}

export const GlobalSearch = () => {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const [inputValue, setInputValue] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(inputValue.trim()), 300);
    return () => clearTimeout(timer);
  }, [inputValue]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        containerRef.current?.querySelector('input')?.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const query = debouncedQuery;

  const canSearchLeads = hasPermission('leads', 'view');
  const canSearchStudents = hasPermission('students', 'view');

  const leadsQuery = useQuery({
    queryKey: ['global-search', 'leads', query],
    queryFn: () => listLeads({ page: 1, pageSize: 5, search: query }),
    enabled: canSearchLeads && query.length >= 2 && open,
  });

  const studentsQuery = useQuery({
    queryKey: ['global-search', 'students', query],
    queryFn: () => listStudents({ page: 1, pageSize: 5, search: query }),
    enabled: canSearchStudents && query.length >= 2 && open,
  });

  const options = useMemo<SearchOption[]>(() => {
    const leads = (leadsQuery.data?.data ?? []).map<SearchOption>((lead) => ({
      type: 'Lead',
      id: lead.id,
      label: `${lead.firstName} ${lead.lastName}`,
      secondary: `${lead.leadNumber} · ${lead.phone}`,
      path: `/leads/${lead.id}`,
    }));
    const students = (studentsQuery.data?.data ?? []).map<SearchOption>((student) => ({
      type: 'Student',
      id: student.id,
      label: `${student.firstName} ${student.lastName}`,
      secondary: `${student.studentNumber} · ${student.phone}`,
      path: `/students/${student.id}`,
    }));
    return [...leads, ...students];
  }, [leadsQuery.data, studentsQuery.data]);

  const isLoading = leadsQuery.isFetching || studentsQuery.isFetching;

  // Nothing this role may search (e.g. Finance Admin, Phase 96).
  if (!canSearchLeads && !canSearchStudents) return null;

  return (
    <Autocomplete<SearchOption>
      ref={containerRef}
      sx={{ width: { xs: '100%', sm: 320, md: 400 } }}
      size="small"
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={options}
      groupBy={(option) => option.type}
      filterOptions={(x) => x}
      loading={isLoading}
      noOptionsText={query.length < 2 ? 'Type to search leads & students…' : 'No matches'}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, value) => option.type === value.type && option.id === value.id}
      inputValue={inputValue}
      onInputChange={(_e, value) => setInputValue(value)}
      onChange={(_e, value) => {
        if (value) {
          navigate(value.path);
          setInputValue('');
        }
      }}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={`${option.type}-${option.id}`}>
          {option.type === 'Lead' ? (
            <ContactsIcon fontSize="small" sx={{ mr: 1.5, color: 'text.secondary' }} />
          ) : (
            <SchoolIcon fontSize="small" sx={{ mr: 1.5, color: 'text.secondary' }} />
          )}
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" noWrap>
              {option.label}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {option.secondary}
            </Typography>
          </Box>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Search leads, students… (Ctrl+K)"
          InputProps={{
            ...params.InputProps,
            startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
            endAdornment: (
              <>
                {isLoading ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};
